import React from "react";
import {
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState
} from "react";
import ClearIcon from "@mui/icons-material/Clear";
import { 
  Collapse, 
  styled,
 } from "@mui/material";
import ActionButton from "controls/global/action-button";
import {
  colors,
  gaps,
  margin
} from "theme/defaultStyle";
import {
  DateTypeCode,
  PageType,
} from "utils/data/enum";
import DatePickerSearch from "../datepicker-search/DatePickerSearch";
import DateTypeField from "./DateTypeField";
import useStewartTableStore from "utils/context/StewartTableContext";
import OpenCheckbox from "./OpenCheckbox";

type Props<T> = {
  open: boolean;
  isDisableFilters?: boolean;
  handleFilterChange: (name: keyof T, value: any, execSearch: boolean) => void;
  page?: PageType;
};

export interface AdvancedSearchHandle {
  reset: () => void;
}

const StyledCollapse = styled(Collapse)({
  "&.MuiCollapse-hidden": {
    display: "none",
  },
});

const InnerContainer = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  justifyContent: "flex-Start",
  gap: gaps.large1,
  "& > :nth-child(1)": {
    flexGrow: 1,
    width: "unset",
    maxWidth: "200px",
  },
  "& > :nth-child(2)": {
    width: "172px",
    minWidth: "400px",
  },
  "& > :nth-child(3)": {
    width: "auto",
    minWidth: "100px",
  },
  "& > :nth-child(4)": {
    width: "unset",
  },
  "& > :nth-child(5)": {
    justifyContent: "flex-end",
  },
  "& .ActionButton-root.Mui-disabled": {
    backgroundColor: "#e5e5e5",
    background: "none",
    boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.25)",
  },
}));

const DateRangeContainer = styled("div")({
  alignItems: "center",
  display: "flex",
});

const Dash = styled("div")({
  background: colors.grey11,
  height: "3px",
  margin: margin.small1,
  width: "20px",
});

const AdvancedSearch = forwardRef(function AdvancedSearch<T extends object>({ open, handleFilterChange, page, isDisableFilters }: Props<T>, ref: React.Ref<AdvancedSearchHandle>) {
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [checked, setChecked] = useState<boolean>(false);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [dateType, setDateType] = useState<string>("");
  const [dateFromError, setDateFromError] = useState<boolean>(false);
  const [dateToError, setDateToError] = useState<boolean>(false);
  const [dateReset, setDateReset] = useState<number>(0);
  const [, { setMinDate, setMaxDate }] = useStewartTableStore();
  const [, { setShowToast }] = useStewartTableStore();


  const shouldSearch = (date: Date | null, searchIfNull: boolean = true) =>
    date ? !isNaN(date.getTime()) : searchIfNull;

  const handleFilter = (name: keyof T, date: Date | null) =>
    handleFilterChange(name, date ?? undefined, shouldSearch(date));

  const handleDateFromChange = (newDate: Date | null) => {
    setDateFrom(newDate);
    setDateToError(false);
    handleFilter("dateRangeStart" as keyof T, newDate);
    setMinDate(newDate);
  };

  const handleDateToChange = (newDate: Date | null) => {
    setDateTo(newDate);
    setDateFromError(false);
    handleFilter("dateRangeEnd" as keyof T, newDate);
    setMaxDate(newDate);
  };

  const updateDateType = (page?: PageType) => {
    try {
      if (page === PageType.AssignPage || page === PageType.LookupPage) {
        setDateType(DateTypeCode.Assigned);
      } else {
        setDateType(DateTypeCode.Modified);
      }
    } catch (error) {
      console.error("Error updating date type:", error);
    }
  };

  const handleSetDateFromError = (error: boolean) => {
    setDateFromError(error);

  }
  const handleSetDateToError = (error: boolean) => {
    setDateToError(error);
  }

  const handleDateTypeChange = (name: keyof T, value: any, execSearch: boolean) => {
    execSearch = shouldSearch(dateTo, false) || shouldSearch(dateFrom, false);
    setDateType(value);
    handleFilterChange(name, value, execSearch);
  };

  const handleResetForDefaultPages = () => {
    setDateFrom(null);
    setDateTo(null);
    updateDateType(page);
    setDateReset(dateReset + 1);
    setChecked(false);
    handleFilterChange("dateRangeStart" as keyof T, null, false);
    handleFilterChange("dateRangeEnd" as keyof T, null, false);
    if (page == PageType.LookupPage) handleFilterChange("isInventory" as keyof T, 0, false);
    handleFilterChange("dateTypeCode" as keyof T, DateTypeCode.Assigned, true);
    setMaxDate(null);
    setMinDate(null);
  };

  const handleCheckboxChange = (
    name: keyof T,
    value: any,
    execSearch: boolean
  ) => {
    if (execSearch) {
      setDateFromError(false);
      setDateToError(false);
    }
    setChecked(value);
    handleFilterChange(name, value, execSearch);
  };

  useImperativeHandle(ref, () => ({
    reset: handleResetForDefaultPages
  }));

  useEffect(() => {
    updateDateType(page);
  }, []);

  useEffect(() => {
    setMaxDate(null);
    setMinDate(null);
    setShowToast(false);
  }, [PageType, page]);
  
  return (
    <StyledCollapse
      in={open}
      data-test="advancedSearchPanel"
    >
      <InnerContainer>
        <DateTypeField
          dateType={dateType}
          data-test="dateType"
          variant="outlined"
          handleDateTypeChange={handleDateTypeChange}
          page={page ? page : PageType.AssignPage}
          disabled={ page !== PageType.LookupPage}
        />
        <DateRangeContainer>
          <DatePickerSearch
            data-test="dateFrom"
            value={dateFrom}
            maxDate={dateTo ?? new Date()}
            handleDateChange={handleDateFromChange}
            hasError={dateFromError}
            reset={dateReset}
            onError={handleSetDateFromError}
            disabled={isDisableFilters}
          />
          <Dash />
          <DatePickerSearch
            data-test="dateTo"
            value={dateTo}
            minDate={dateFrom ?? new Date()}
            handleDateChange={handleDateToChange}
            hasError={dateToError}
            reset={dateReset}
            onError={handleSetDateToError}
            disabled={isDisableFilters}
          />
        </DateRangeContainer>
        {page === PageType.LookupPage && (
          <OpenCheckbox
            {...{ handleCheckboxChange, checked }}
            data-test="openCheckbox"
          />
        )}
        <ActionButton
         disabled={
          isDisableFilters ||
          (!dateFrom && !dateTo && !checked) 
        }
          variant="outlined"
          color="secondary"
          startIcon={<ClearIcon />}
          onClick={handleResetForDefaultPages}
        >
          Clear
        </ActionButton>
      </InnerContainer>
    </StyledCollapse>
  );
});

export default AdvancedSearch;
